import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const SupportShell = ({ title, breadcrumb, children, testId }) => (
  <div data-testid={testId} className="pt-[100px] pb-16">
    <div className="max-w-[1000px] mx-auto px-4 md:px-12">
      <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mb-4">
        HOME / SUPPORT / {breadcrumb}
      </p>
      <h1 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl leading-none text-center mb-12">
        {title}
      </h1>
      <div className="space-y-6 font-body text-lg md:text-xl text-ink/85 leading-relaxed">
        {children}
      </div>
    </div>
  </div>
);

const FAQ_ITEMS = [
  {
    q: "Why are pieces 1-of-1?",
    a: "Our exclusives are made once from rescued or vintage material. The material itself decides what we can build, and there's never enough to repeat a piece. That's the point.",
  },
  {
    q: "How does sizing run?",
    a: "Staples run true to size. Bottoms run slightly oversized. If you're between sizes, size down. Detailed size chart on every product page.",
  },
  {
    q: "Do you ship internationally?",
    a: "Yes. International shipping is calculated at checkout. Customs and duties are the buyer's responsibility.",
  },
  {
    q: "What's your return policy on exclusives?",
    a: "Exclusives are final sale. Staples are returnable within 14 days, unworn, with tags. See Returns for the full policy.",
  },
  {
    q: "How long does an order take?",
    a: "Staples ship within 3-5 days. Exclusives ship within 7-14 days because each one is hand-finished after order.",
  },
  {
    q: "Can I commission a custom piece?",
    a: "Yes. We take a small number of custom commissions each season. Use the contact form with 'Custom piece' selected.",
  },
];

export const FAQPage = () => {
  const [open, setOpen] = useState(0);
  return (
    <SupportShell title="FAQ" breadcrumb="FAQ" testId="page-faq">
      <div className="border-t border-ink/12">
        {FAQ_ITEMS.map((item, i) => (
          <div key={item.q} className="border-b border-ink/12">
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              data-testid={`faq-item-${i}`}
              className="w-full text-left py-5 flex items-center justify-between gap-4 font-display uppercase text-xl md:text-2xl"
              aria-expanded={open === i}
            >
              <span>{item.q}</span>
              {open === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {open === i && (
              <p className="pb-6 font-body text-lg text-ink/85">{item.a}</p>
            )}
          </div>
        ))}
      </div>
    </SupportShell>
  );
};

export const SizeGuidePage = () => (
  <SupportShell title="Size Guide" breadcrumb="SIZE GUIDE" testId="page-size-guide">
    <p>All measurements in inches. Pieces are cut for a relaxed, slightly oversized fit. If between sizes, size down.</p>
    <div className="overflow-x-auto rounded-[20px] shadow-card mt-6">
      <table className="w-full font-mono text-xs uppercase">
        <thead className="bg-ink text-cream">
          <tr>
            <th className="text-left px-4 py-3">SIZE</th>
            <th className="text-left px-4 py-3">WAIST</th>
            <th className="text-left px-4 py-3">HIP</th>
            <th className="text-left px-4 py-3">INSEAM</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["S", "28-30", "36-38", "30"],
            ["M", "30-32", "38-40", "31"],
            ["L", "32-34", "40-42", "32"],
            ["XL", "34-36", "42-44", "32"],
          ].map((row) => (
            <tr key={row[0]} className="border-t border-ink/12">
              {row.map((c, i) => (
                <td key={i} className="px-4 py-3">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-6">For jackets, refer to chest measurement on each product page. Leather pieces stretch slightly with wear.</p>
  </SupportShell>
);

export const ShippingPage = () => (
  <SupportShell title="Shipping" breadcrumb="SHIPPING" testId="page-shipping">
    <p>We ship from Bangalore, India. Each piece is inspected, signed (where applicable) and shipped in recycled packaging.</p>
    <h2 className="font-display uppercase text-3xl mt-8 mb-3 text-ink">Within India</h2>
    <p>Standard shipping (5-7 working days) is free on orders over &#8377;5,000. Express shipping (2-3 working days) is &#8377;450.</p>
    <h2 className="font-display uppercase text-3xl mt-8 mb-3 text-ink">International</h2>
    <p>Calculated at checkout based on destination and weight. Delivery in 7-21 working days depending on country. Customs and duties are the buyer's responsibility.</p>
    <h2 className="font-display uppercase text-3xl mt-8 mb-3 text-ink">Exclusives</h2>
    <p>Exclusives ship within 7-14 days of order because they are hand-finished and signed after purchase.</p>
  </SupportShell>
);

export const ReturnsPage = () => (
  <SupportShell title="Returns" breadcrumb="RETURNS" testId="page-returns">
    <p>We want you to live in the pieces, but if something is off, we'll work with you.</p>
    <h2 className="font-display uppercase text-3xl mt-8 mb-3 text-ink">Staples</h2>
    <p>Returnable within 14 days of delivery, unworn, with original tags. Email hello@damageculture.in with your order number to begin a return. We'll send a prepaid label for India returns.</p>
    <h2 className="font-display uppercase text-3xl mt-8 mb-3 text-ink">Exclusives</h2>
    <p>All exclusives are final sale. Each is made for the person who ordered it, after the order. We don't restock.</p>
    <h2 className="font-display uppercase text-3xl mt-8 mb-3 text-ink">Damaged on arrival</h2>
    <p>Photograph everything, email us within 48 hours. We'll repair, replace, or refund. Hand-distressing on the garment is not a defect, that is the design.</p>
  </SupportShell>
);

const TRACK_STAGES = [
  { key: "ORDER_PLACED", label: "Order placed" },
  { key: "CONFIRMED", label: "Payment confirmed" },
  { key: "PACKED", label: "Packed" },
  { key: "PICKED_UP", label: "Picked up" },
  { key: "IN_TRANSIT", label: "In transit" },
  { key: "OUT_FOR_DELIVERY", label: "Out for delivery" },
  { key: "DELIVERED", label: "Delivered" },
];

export const TrackOrderPage = () => {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTracking = async (id, em) => {
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const axios = (await import("axios")).default;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const url = `${API}/order/${encodeURIComponent(id.trim())}/tracking${em ? `?email=${encodeURIComponent(em)}` : ""}`;
      const { data } = await axios.get(url);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill from query string when the customer lands here from the order
  // confirmation email / page (e.g. ?order=DC01047&email=foo%40bar.com)
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const o = sp.get("order");
    const e = sp.get("email");
    if (o) {
      setOrderId(o);
      if (e) setEmail(e);
      fetchTracking(o, e || "");
    }
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!orderId) return;
    fetchTracking(orderId, email);
  };

  const currentIdx = result
    ? Math.max(
        0,
        TRACK_STAGES.findIndex((s) => s.key === result.current_stage)
      )
    : -1;

  return (
    <SupportShell title="Track Order" breadcrumb="TRACK ORDER" testId="page-track-order">
      <p>Enter your order number and the email you used at checkout.</p>
      <form onSubmit={submit} className="space-y-3 mt-6 max-w-md" data-testid="track-form">
        <input
          className="dc-input"
          placeholder="ORDER NUMBER (E.G. DC01042)"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          data-testid="track-order-id"
          required
        />
        <input
          className="dc-input"
          type="email"
          placeholder="EMAIL ON ORDER"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="track-order-email"
        />
        <button className="btn-solid" type="submit" disabled={loading} data-testid="track-order-submit">
          {loading ? "CHECKING..." : "Track"}
        </button>
      </form>

      {error && (
        <p className="font-mono text-xs uppercase tracking-widest mt-6 text-dc-red" data-testid="track-error">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-10 max-w-2xl" data-testid="track-result">
          {/* Header card */}
          <div className="card-surface rounded-[20px] shadow-card p-6 md:p-7">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-ink/60">
              ORDER {result.order_id}
            </p>
            <h2 className="font-display uppercase text-3xl md:text-4xl mt-1">
              {(result.current_status || result.order_status || "ORDER PLACED").toUpperCase()}
            </h2>
            <div className="hairline mt-5 mb-5" />
            <div className="grid grid-cols-2 gap-4 font-mono text-xs">
              <div>
                <p className="text-ink/55 uppercase tracking-widest text-[10px]">Courier</p>
                <p className="mt-1 font-bold">{result.courier_name || "Assigning..."}</p>
              </div>
              <div>
                <p className="text-ink/55 uppercase tracking-widest text-[10px]">AWB</p>
                <p className="mt-1 font-bold">{result.awb || "—"}</p>
              </div>
              <div>
                <p className="text-ink/55 uppercase tracking-widest text-[10px]">ETA</p>
                <p className="mt-1 font-bold">{result.eta || "—"}</p>
              </div>
              <div>
                <p className="text-ink/55 uppercase tracking-widest text-[10px]">Payment</p>
                <p className="mt-1 font-bold text-dc-red">{(result.payment_status || "paid").toUpperCase()}</p>
              </div>
            </div>
            {result.track_url && (
              <a
                href={result.track_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost mt-6 inline-flex"
                data-testid="track-courier-link"
              >
                Track on courier site &rarr;
              </a>
            )}
          </div>

          {/* Timeline */}
          <ol className="mt-8 relative pl-7">
            <span className="absolute left-[10px] top-2 bottom-2 w-px bg-ink/10" />
            {TRACK_STAGES.map((stg, i) => {
              const reached = result.timeline?.find((t) => t.stage === stg.key)?.reached;
              const isCurrent = i === currentIdx;
              return (
                <li key={stg.key} className="relative mb-5 last:mb-0">
                  <span
                    className={`absolute -left-7 top-[3px] w-5 h-5 rounded-full border-2 ${
                      reached
                        ? "bg-dc-red border-dc-red"
                        : "bg-cream border-ink/20"
                    } ${isCurrent ? "ring-4 ring-dc-red/20" : ""}`}
                  />
                  <p
                    className={`font-display uppercase text-base md:text-lg ${
                      reached ? "text-ink" : "text-ink/35"
                    }`}
                  >
                    {stg.label}
                  </p>
                </li>
              );
            })}
          </ol>

          {/* Recent scans */}
          {result.scans && result.scans.length > 0 && (
            <div className="mt-8 card-surface rounded-[20px] shadow-card p-6">
              <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-ink/60 mb-3">
                ACTIVITY
              </p>
              <ul className="space-y-3">
                {result.scans.slice(0, 8).map((s, i) => (
                  <li key={`${s.date}-${i}`} className="font-mono text-xs">
                    <p className="text-ink">{s.activity}</p>
                    <p className="text-ink/55 mt-0.5">
                      {s.date} {s.location ? `· ${s.location}` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </SupportShell>
  );
};
